import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { X_HOHR_CAPTCHA_TOKEN } from '@app/core/constants/recaptcha.constant';
import { Success, VerificationChannel } from '@app/core/types/membership/membership.types';
import { AccountState } from '@app/core/types/account-state.types';
import {
  LinkEmailRequest,
  LinkPhoneRequest,
  RegisterAccountRequest,
} from '../model/registration.types';

@Injectable({ providedIn: 'root' })
export class RegistrationService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.url.api}/identity/me`;

  getAccountState(): Observable<AccountState> {
    return this.http.get<AccountState>(`${this.url}/account-state`);
  }

  startPhoneVerification(
    phoneNumber: string,
    captchaToken: string,
    channel: VerificationChannel
  ): Observable<void> {
    return this.http.post<void>(
      `${this.url}/phonenumber/verification/start`,
      { phoneNumber, channel },
      { headers: { [X_HOHR_CAPTCHA_TOKEN]: captchaToken } }
    );
  }

  checkPhoneVerification(phoneNumber: string, verificationCode: string): Observable<boolean> {
    return this.http
      .post<Success>(`${this.url}/phonenumber/verification/check`, { phoneNumber, verificationCode })
      .pipe(map((response) => response.success));
  }

  checkPhoneExists(phoneNumber: string): Observable<boolean> {
    return this.http
      .post<{ exists: boolean }>(`${this.url}/phonenumber/exists`, { phoneNumber })
      .pipe(map((response) => response.exists));
  }

  startEmailVerification(email: string, captchaToken: string): Observable<void> {
    return this.http.post<void>(
      `${this.url}/email/verification/start`,
      { email },
      { headers: { [X_HOHR_CAPTCHA_TOKEN]: captchaToken } }
    );
  }

  checkEmailVerification(email: string, verificationCode: string): Observable<boolean> {
    return this.http
      .post<Success>(`${this.url}/email/verification/check`, { email, verificationCode })
      .pipe(map((response) => response.success));
  }

  register(request: RegisterAccountRequest): Observable<void> {
    return this.http.post<void>(`${this.url}/register`, request);
  }

  linkEmail(request: LinkEmailRequest): Observable<void> {
    return this.http.post<void>(`${this.url}/email/link`, request);
  }

  linkPhone(request: LinkPhoneRequest): Observable<void> {
    return this.http.post<void>(`${this.url}/phonenumber/link`, request);
  }
}
